import {Opportunity} from "../../../opportunities/model/Opportunity";
import {ActionService} from "../../../opportunity_actions_service/service/ActionService";
import {GroupedOpportunityKey, OpportunityAction} from "../../model/OpportunityAction";
import {IAngularEvent, IComponentController, IComponentOptions, IHttpResponse, IPromise, IScope} from "angular";
import {ACTION_SERVICE_NAME, ToastNotificationServiceName} from "../../../utils/names/serviceNames";
import {ToastNotificationService} from "../../../utils/ToastNotificationService";
import "./action.component.scss";
import {ActionTypes} from "../../../action_status/model/ActionType";
import {PreviewHelpDialog} from "../help/preview-help.dialog";
import {PreviewContentType} from "../help/model/PreviewContentType";
import {Principle} from "modules/brand_service/model/Principle";

export interface ActionComponentScope extends IScope {
    onActionStart: (ev: IAngularEvent) => void;
    opportunity: Opportunity;
    buttonDisabled: () => boolean;
    opportunityActions: OpportunityAction[];
    showHelp: () => void;
    title: string;
    icon: string;
    tooltip: TooltipConfigs;
}

export class TooltipConfigs {
    show: boolean;
    message: string;
}

export abstract class ActionComponent implements IComponentController {

    protected principle: Principle

    protected static instance = (controller: IComponentController,
                                 extraArgs: any[] = []): IComponentOptions => ({
        template: require("./action.component.html"),
        controller: [
            '$scope',
            ACTION_SERVICE_NAME,
            '$exceptionHandler',
            '$mdDialog',
            ToastNotificationServiceName,
            ...extraArgs,
            controller
        ],
        bindings: {
            opportunity: '<',
            onAction: '<',
            principle: '<'
        }
    });

    constructor(protected $scope: ActionComponentScope,
                protected actionService: ActionService,
                protected $exceptionHandler: ng.IExceptionHandlerService,
                protected $mdDialog: angular.material.IDialogService,
                protected toastService: ToastNotificationService) {
        this.$scope.buttonDisabled = () => false;
        this.$scope.onActionStart = this.onActionStart;
    }

    $onInit(): void {
        this.$scope.buttonDisabled = () => this.isDisabled(this.opportunity);
        this.$scope.tooltip = this.initTooltip();
        this.$scope.title = this.getTitle();
        this.$scope.icon = this.getIcon();
        this.$scope.showHelp = this.showHelp;
        this.bindOpportunityToScope();
    }

    private initTooltip() {
        return {
            show: this.$scope.buttonDisabled(),
            message: this.getTooltipMessage()
        };
    }

    private bindOpportunityToScope() {
        try {
            if (this.opportunity && this.onAction) {
                this.$scope.opportunity = this.opportunity;
            } else {
                throw new Error("Opportunity can not be null!");
            }
        } catch (except) {
            this.$exceptionHandler(except, `ActionComponent[${this.constructor.name}] component startup failed!`);
        }
    }

    protected abstract executeAction: () => IPromise<IHttpResponse<OpportunityAction[]>>;

    protected abstract getActionType: () => ActionTypes;

    protected abstract getTitle: () => string;

    protected abstract getIcon: () => string;

    protected getSuccessMessage = () => "Action successful";

    protected getErrorMessage = () => "Failed to perform action!";

    protected getTooltipMessage(): string {
        if (this.opportunity.applicationInfo.isEpo) {
            return "Opportunity is epo";
        } else if (!this.opportunity.customer.employee || !this.opportunity.customer.employee.active) {
            return "Employee is disabled";
        } else if (!this.opportunity.customer.employee || !this.opportunity.customer.employee.licensed) {
            return "Employee is not licensed";
        } else {
            return "Action disabled";
        }
    }

    protected isDisabled(opportunity: Opportunity): boolean {
        return opportunity.applicationInfo.isEpo ||
            (!opportunity.customer.employee) ||
            (!opportunity.customer.employee.active) ||
            (!opportunity.customer.employee.licensed);
    }


    protected opportunity: Opportunity;

    protected onAction: (resultPromise: IPromise<OpportunityAction[]>) => void;

    onActionStart = (ev: ng.IAngularEvent) => {
        this.$scope.buttonDisabled = () => true;
        let resultPromise: IPromise<OpportunityAction[]> = this.executeAction()
            .then((resp) => {
                    this.$scope.buttonDisabled = () => this.isDisabled(this.opportunity);
                    this.notifyNewAction(resp.data);
                    let successMessage = this.getSuccessMessage();
                    this.toastService.generateSuccessMessage(successMessage);
                    return resp.data;
                },
                (reason) => {
                    this.$scope.buttonDisabled = () => this.isDisabled(this.opportunity);
                    if (reason) {
                        let errorMessage = this.getErrorMessage();
                        this.toastService.generateErrorMessage(errorMessage);
                        return [];
                    }
                }
            );
        this.onAction(resultPromise);
    };


    notifyNewAction(resp: OpportunityAction[]) {
        let opportunity = this.$scope.opportunity;
        resp.forEach(action => {
            this.actionService.emitOpportunityActionsGroup({
                key: new GroupedOpportunityKey(opportunity.customer.id, opportunity.calculator.id),
                value: [action]
            });
        })

    }

    showHelp = () => {
        let dialog = PreviewHelpDialog.instance(this.opportunity, this.getActionType(), this.getPreviewContentType());
        this.$mdDialog.show(dialog);
    }

    protected getPreviewContentType = (): PreviewContentType => {
        return PreviewContentType.IFRAME;
    }
}